import { useCallback, useEffect, useRef, useState } from "react";

import {
  createPlayback,
  playheadInsideSpan,
  PLAYBACK_SPEEDS,
  SCENE_SKIP_HOLD_MS,
  skipScenePlayhead,
  tickPlayback,
  type PlaybackBounds,
  type PlaybackState,
  type ScrubDirection,
} from "@/lib/timeline/playback";
import { clampPlayhead, pausePlayback, seekPlayback } from "./playbackSeek";

export interface PlaybackRestore {
  playheadMs: number;
  speed: number;
}

export function useBookTimelinePlayback(
  bounds: PlaybackBounds,
  restore?: PlaybackRestore,
  beats: readonly { startMs: number }[] = [],
  /** A seek to the same scene is on its way, so a span change must not jump. */
  seekPending = false,
  initialRepeat = false,
) {
  const playbackRef = useRef<PlaybackState | null>(null);
  if (playbackRef.current === null) {
    const raw = restore?.playheadMs ?? bounds.startMs;
    playbackRef.current = createPlayback(clampPlayhead(raw, bounds), restore?.speed);
  }
  const [transport, setTransport] = useState({
    playing: false,
    speed: playbackRef.current.speed,
  });
  const [repeat, setRepeat] = useState(initialRepeat);
  const [spanJump, setSpanJump] = useState(0);
  const repeatRef = useRef(initialRepeat);
  repeatRef.current = repeat;
  const spanKey = `${bounds.startMs}:${bounds.endMs}`;
  const spanKeyRef = useRef(spanKey);
  if (spanKeyRef.current !== spanKey) {
    spanKeyRef.current = spanKey;
    const current = playbackRef.current;
    if (!seekPending && !playheadInsideSpan(current.playheadMs, bounds)) {
      playbackRef.current = {
        ...current,
        playing: false,
        playheadMs: bounds.startMs,
      };
      setSpanJump((count) => count + 1);
      if (current.playing) {
        setTransport({ playing: false, speed: current.speed });
      }
    }
  }

  useEffect(() => {
    const current = playbackRef.current;
    if (current === null) {
      return;
    }
    const playheadMs = clampPlayhead(current.playheadMs, bounds);
    if (playheadMs !== current.playheadMs) {
      playbackRef.current = { ...current, playheadMs };
    }
  }, [bounds.startMs, bounds.endMs]);

  const getPlayback = useCallback((): PlaybackState => {
    const current = playbackRef.current;
    if (current === null) {
      return createPlayback(bounds.startMs);
    }
    return current;
  }, [bounds.startMs]);

  const onTick = useCallback(
    (deltaMs: number): void => {
      const prev = playbackRef.current;
      if (prev === null) {
        return;
      }
      const next = tickPlayback(prev, deltaMs, bounds, repeatRef.current);
      playbackRef.current = next;
      if (next.playing !== prev.playing || next.speed !== prev.speed) {
        setTransport({ playing: next.playing, speed: next.speed });
      }
    },
    [bounds],
  );

  const togglePlay = (): void => {
    const current = playbackRef.current;
    if (current === null) {
      return;
    }
    const playing = !current.playing;
    let playheadMs = current.playheadMs;
    if (playing && playheadMs >= bounds.endMs) {
      playheadMs = bounds.startMs;
    }
    playbackRef.current = { ...current, playing, playheadMs };
    setTransport({ playing, speed: current.speed });
  };

  const restart = (): void => {
    const current = playbackRef.current;
    if (current === null) {
      return;
    }
    playbackRef.current = {
      ...current,
      playheadMs: bounds.startMs,
      skipHoldMs: 0,
    };
  };

  const setSpeed = (speed: number): void => {
    if (!PLAYBACK_SPEEDS.includes(speed as (typeof PLAYBACK_SPEEDS)[number])) {
      return;
    }
    const current = playbackRef.current;
    if (current === null) {
      return;
    }
    playbackRef.current = { ...current, speed };
    setTransport({ playing: current.playing, speed });
  };

  const toggleRepeat = (): void => {
    setRepeat((value) => !value);
  };

  const setScrub = (scrub: ScrubDirection): void => {
    const current = playbackRef.current;
    if (current === null) {
      return;
    }
    playbackRef.current = { ...current, scrub };
  };

  const skipScene = (direction: -1 | 1): void => {
    const current = playbackRef.current;
    if (current === null) {
      return;
    }
    playbackRef.current = {
      ...current,
      playheadMs: skipScenePlayhead(
        beats, current.playheadMs, direction, bounds,
      ),
      skipHoldMs: SCENE_SKIP_HOLD_MS,
    };
  };

  return {
    getPlayback,
    onTick,
    transport,
    togglePlay,
    restart,
    setSpeed,
    setScrub,
    pause: () => pausePlayback(playbackRef, setTransport),
    seekTo: (playheadMs: number) => seekPlayback(playbackRef, bounds, playheadMs),
    skipScene,
    repeat,
    toggleRepeat,
    spanJump,
  };
}
